import React from 'react';
import Icon from '../../../components/AppIcon';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line } from 'recharts';

const MessageAnalytics = ({ 
  analytics = {}, 
  className = '' 
}) => {
  const {
    totalMessages = 0,
    deliveredMessages = 0,
    failedMessages = 0,
    pendingMessages = 0,
    channelBreakdown = [],
    monthlyTrends = [],
    successRate = 0
  } = analytics;

  const statusData = [
    { name: 'Delivered', value: deliveredMessages, color: '#10B981' },
    { name: 'Failed', value: failedMessages, color: '#EF4444' },
    { name: 'Pending', value: pendingMessages, color: '#F59E0B' }
  ];

  const channelData = channelBreakdown?.map(item => ({
    name: item?.channel,
    messages: item?.count,
    successRate: item?.successRate
  }));

  const StatCard = ({ icon, title, value, subtitle, color = 'text-primary' }) => (
    <div className="bg-surface border border-border rounded-lg p-4 hover:shadow-card transition-micro">
      <div className="flex items-center space-x-3">
        <div className={`w-12 h-12 ${color?.replace('text-', 'bg-')}/10 rounded-lg flex items-center justify-center`}>
          <Icon name={icon} size={20} className={color} />
        </div>
        <div className="flex-1">
          <p className="text-2xl font-bold text-text-primary">{value}</p>
          <p className="text-sm font-medium text-text-secondary">{title}</p>
          {subtitle && (
            <p className="text-xs text-text-secondary mt-1">{subtitle}</p>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Overview Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon="Send"
          title="Total Messages"
          value={totalMessages?.toLocaleString()}
          subtitle="All time"
          color="text-primary"
        />
        <StatCard
          icon="CheckCircle"
          title="Delivered"
          value={deliveredMessages?.toLocaleString()}
          subtitle={`${((deliveredMessages / totalMessages) * 100)?.toFixed(1)}% success rate`}
          color="text-success"
        />
        <StatCard
          icon="XCircle"
          title="Failed"
          value={failedMessages?.toLocaleString()}
          subtitle={`${((failedMessages / totalMessages) * 100)?.toFixed(1)}% failure rate`}
          color="text-error"
        />
        <StatCard
          icon="Clock"
          title="Pending"
          value={pendingMessages?.toLocaleString()}
          subtitle="In progress"
          color="text-warning"
        />
      </div>
      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Delivery Status Pie Chart */}
        <div className="bg-surface border border-border rounded-lg p-6">
          <div className="flex items-center space-x-2 mb-4">
            <Icon name="PieChart" size={20} className="text-text-secondary" />
            <h3 className="text-lg font-semibold text-text-primary">Delivery Status</h3>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={statusData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  paddingAngle={5}
                  dataKey="value"
                >
                  {statusData?.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry?.color} />
                  ))}
                </Pie>
                <Tooltip 
                  formatter={(value, name) => [value, name]}
                  labelStyle={{ color: '#1E293B' }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="flex justify-center space-x-6 mt-4">
            {statusData?.map((item, index) => (
              <div key={index} className="flex items-center space-x-2">
                <div 
                  className="w-3 h-3 rounded-full" 
                  style={{ backgroundColor: item?.color }}
                />
                <span className="text-sm text-text-secondary">{item?.name}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Channel Performance Bar Chart */}
        <div className="bg-surface border border-border rounded-lg p-6">
          <div className="flex items-center space-x-2 mb-4">
            <Icon name="BarChart3" size={20} className="text-text-secondary" />
            <h3 className="text-lg font-semibold text-text-primary">Channel Performance</h3>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={channelData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis 
                  dataKey="name" 
                  stroke="#64748B"
                  fontSize={12}
                />
                <YAxis 
                  stroke="#64748B"
                  fontSize={12}
                />
                <Tooltip 
                  contentStyle={{
                    backgroundColor: '#FFFFFF',
                    border: '1px solid #E2E8F0',
                    borderRadius: '8px'
                  }}
                />
                <Bar 
                  dataKey="messages" 
                  fill="#2563EB" 
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
      {/* Monthly Trends */}
      <div className="bg-surface border border-border rounded-lg p-6">
        <div className="flex items-center space-x-2 mb-4">
          <Icon name="TrendingUp" size={20} className="text-text-secondary" />
          <h3 className="text-lg font-semibold text-text-primary">Monthly Message Trends</h3>
        </div>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={monthlyTrends}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis 
                dataKey="month" 
                stroke="#64748B"
                fontSize={12}
              />
              <YAxis 
                stroke="#64748B"
                fontSize={12}
              />
              <Tooltip 
                contentStyle={{
                  backgroundColor: '#FFFFFF',
                  border: '1px solid #E2E8F0',
                  borderRadius: '8px'
                }}
              />
              <Line 
                type="monotone" 
                dataKey="messages" 
                stroke="#2563EB" 
                strokeWidth={3}
                dot={{ fill: '#2563EB', strokeWidth: 2, r: 4 }}
                activeDot={{ r: 6, stroke: '#2563EB', strokeWidth: 2 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
      {/* Success Rate by Channel */}
      <div className="bg-surface border border-border rounded-lg p-6">
        <div className="flex items-center space-x-2 mb-4">
          <Icon name="Target" size={20} className="text-text-secondary" />
          <h3 className="text-lg font-semibold text-text-primary">Success Rate by Channel</h3>
        </div>
        <div className="space-y-4">
          {channelBreakdown?.map((channel, index) => (
            <div key={index} className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <Icon 
                  name={channel?.channel === 'email' ? 'Mail' : channel?.channel === 'sms' ? 'MessageSquare' : 'MessageCircle'} 
                  size={16} 
                  className="text-text-secondary" 
                />
                <span className="font-medium text-text-primary capitalize">
                  {channel?.channel}
                </span>
              </div>
              <div className="flex items-center space-x-3">
                <div className="w-32 bg-muted rounded-full h-2">
                  <div 
                    className="bg-success h-2 rounded-full transition-all duration-300"
                    style={{ width: `${channel?.successRate}%` }}
                  />
                </div>
                <span className="text-sm font-medium text-text-primary w-12 text-right">
                  {channel?.successRate}%
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default MessageAnalytics;